import React, { useState } from 'react';

import PropTypes from 'prop-types';

import { Add, Remove } from '@mui/icons-material';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';

import BaseModal from '@/components/molecules/modals/BaseModal';
import { useEnterpiseVisitRulesApi } from '@/services/enterpise-visits-rules';

import { ActionButton, CancelButton, NumberInput, PeriodButton, SaveButton } from './styles';

export const AddVisitRuleModal = ({ open, onClose, idEnterprise, collaborators, properties, onSuccess }) => {
    const { createVisitRule } = useEnterpiseVisitRulesApi();

    const [selectedCollaborator, setSelectedCollaborator] = useState(null);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [requiredVisits, setRequiredVisits] = useState(0);
    const [allowedVisits, setAllowedVisits] = useState(1);
    const [period, setPeriod] = useState('fulltime');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [propertyList, setPropertyList] = useState(properties || []);

    const handleClose = () => {
        if (isSubmitting) return;

        // Reset form
        setSelectedCollaborator(null);
        setSelectedProperty(null);
        setRequiredVisits(0);
        setAllowedVisits(1);
        setPeriod('fulltime');
        setErrors({});

        onClose();
    };

    const handleSelectCollaborator = (newValue) => {
        setSelectedCollaborator(newValue);
        setSelectedProperty(null);
        // Filtra propriedades associadas ao colaborador selecionado
        if (newValue) {
            const filteredProperties = properties.filter((property) => property.collaborators.some((collab) => collab.id === newValue.id));
            setPropertyList(filteredProperties);
        } else {
            setPropertyList(properties || []);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!selectedCollaborator) {
            newErrors.collaborator = 'Selecione um técnico';
        }

        if (!selectedProperty) {
            newErrors.property = 'Selecione uma fazenda';
        }

        if (requiredVisits < 0) {
            newErrors.requiredVisits = 'Valor deve ser maior ou igual a 0';
        }

        if (requiredVisits > 100) {
            newErrors.requiredVisits = 'Valor deve ser menor ou igual a 100';
        }

        if (allowedVisits < 1) {
            newErrors.allowedVisits = 'Valor deve ser maior ou igual a 1';
        }

        if (allowedVisits > 100) {
            newErrors.allowedVisits = 'Valor deve ser menor ou igual a 100';
        }

        // Validação: required_visits não pode ser maior que allowed_visits
        if (requiredVisits > allowedVisits) {
            newErrors.requiredVisits = 'Visitas obrigatórias não podem ser maiores que visitas permitidas';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            await createVisitRule({
                enterprise_id: idEnterprise,
                collaborator_id: selectedCollaborator.id,
                property_id: selectedProperty.id,
                required_visits: requiredVisits,
                allowed_visits: allowedVisits,
                period: period
            });

            onSuccess?.();
            handleClose();
        } catch (error) {
            console.error('Erro ao criar regra de visita:', error);
            setErrors({
                submit: 'Erro ao criar regra de visita. Tente novamente.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleIncrement = (field) => {
        if (field === 'required') {
            // Validação: required_visits não pode ser maior que allowed_visits
            setRequiredVisits((prev) => {
                const newValue = prev + 1;
                if (newValue > allowedVisits || newValue > 100) {
                    return prev; // Não incrementa se ultrapassar allowed_visits ou 100
                }
                return newValue;
            });
        } else {
            setAllowedVisits((prev) => {
                const newValue = prev + 1;
                if (newValue > 100) {
                    return prev; // Não incrementa se ultrapassar 100
                }
                return newValue;
            });
        }
    };

    const handleDecrement = (field) => {
        if (field === 'required') {
            setRequiredVisits((prev) => Math.max(0, prev - 1));
        } else {
            // Validação: allowed_visits não pode ser menor que required_visits
            setAllowedVisits((prev) => {
                const newValue = Math.max(1, prev - 1);
                if (newValue < requiredVisits) {
                    return prev; // Não decrementa se ficar menor que required_visits
                }
                return newValue;
            });
        }
    };

    const handlePeriodToggle = () => {
        setPeriod((prev) => (prev === 'halftime' ? 'fulltime' : 'halftime'));
    };

    const modalActions = (
        <>
            <CancelButton variant="outlined" onClick={handleClose} disabled={isSubmitting}>
                Cancelar
            </CancelButton>
            <SaveButton variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Salvando...' : 'Salvar'}
            </SaveButton>
        </>
    );

    return (
        <BaseModal title="Cadastro padrão de visitas" open={open} handleClose={handleClose} withActions={modalActions} maxWidth="md">
            <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" gap={2} sx={{ flexWrap: 'wrap' }}>
                    {/* Técnico */}
                    <Box flex={1}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Técnico
                        </Typography>
                        <Autocomplete
                            options={collaborators || []}
                            getOptionLabel={(option) => option.name || ''}
                            value={selectedCollaborator}
                            onChange={(_, newValue) => {
                                handleSelectCollaborator(newValue);
                                setErrors((prev) => ({ ...prev, collaborator: null }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Selecione o técnico"
                                    size="small"
                                    error={!!errors.collaborator}
                                    helperText={errors.collaborator}
                                />
                            )}
                            disabled={isSubmitting}
                        />
                    </Box>

                    {/* Fazenda */}
                    <Box flex={1}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Fazenda
                        </Typography>
                        <Autocomplete
                            options={propertyList || []}
                            getOptionLabel={(option) => option.name || ''}
                            value={selectedProperty}
                            onChange={(_, newValue) => {
                                setSelectedProperty(newValue);
                                setErrors((prev) => ({ ...prev, property: null }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Selecione a fazenda"
                                    size="small"
                                    error={!!errors.property}
                                    helperText={errors.property}
                                />
                            )}
                            disabled={isSubmitting}
                        />
                    </Box>
                </Box>

                {/* Visitas obrigatórias */}
                <Box display="flex" gap={3} justifyContent="space-between" sx={{ flexWrap: 'wrap' }}>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Visitas obrigatórias no mês
                        </Typography>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <ActionButton
                                size="small"
                                onClick={() => handleDecrement('required')}
                                disabled={requiredVisits <= 0 || isSubmitting}
                            >
                                <Remove fontSize="small" />
                            </ActionButton>
                            <NumberInput
                                value={requiredVisits}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value, 10);
                                    if (!isNaN(val) && val >= 0 && val <= 100) {
                                        // Validação: required_visits não pode ser maior que allowed_visits
                                        if (val <= allowedVisits) {
                                            setRequiredVisits(val);
                                            setErrors((prev) => ({ ...prev, requiredVisits: null }));
                                        }
                                    }
                                }}
                                type="number"
                                variant="outlined"
                                size="small"
                                inputProps={{ min: 0, max: 100, style: { width: '60px' } }}
                                disabled={isSubmitting}
                                error={!!errors.requiredVisits}
                            />
                            <ActionButton
                                size="small"
                                onClick={() => handleIncrement('required')}
                                disabled={isSubmitting || requiredVisits >= allowedVisits || requiredVisits >= 100}
                            >
                                <Add fontSize="small" />
                            </ActionButton>
                        </Box>
                        {errors.requiredVisits && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {errors.requiredVisits}
                            </Typography>
                        )}
                    </Box>

                    {/* Visitas permitidas */}
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Visitas permitidas no mês
                        </Typography>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <ActionButton
                                size="small"
                                onClick={() => handleDecrement('allowed')}
                                disabled={allowedVisits <= 1 || isSubmitting || allowedVisits <= requiredVisits}
                            >
                                <Remove fontSize="small" />
                            </ActionButton>
                            <NumberInput
                                value={allowedVisits}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value, 10);
                                    if (!isNaN(val) && val >= 1 && val <= 100) {
                                        // Validação: allowed_visits não pode ser menor que required_visits
                                        if (val >= requiredVisits) {
                                            setAllowedVisits(val);
                                            setErrors((prev) => ({ ...prev, allowedVisits: null }));
                                        }
                                    }
                                }}
                                type="number"
                                variant="outlined"
                                size="small"
                                inputProps={{ min: 1, max: 100, style: { width: '60px' } }}
                                disabled={isSubmitting}
                                error={!!errors.allowedVisits}
                            />
                            <ActionButton
                                size="small"
                                onClick={() => handleIncrement('allowed')}
                                disabled={isSubmitting || allowedVisits >= 100}
                            >
                                <Add fontSize="small" />
                            </ActionButton>
                        </Box>
                        {errors.allowedVisits && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {errors.allowedVisits}
                            </Typography>
                        )}
                    </Box>

                    {/* Período */}
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Período da visita
                        </Typography>
                        <Box display="flex" alignItems="center" gap={2}>
                            <PeriodButton
                                className={period === 'halftime' ? 'active' : ''}
                                onClick={handlePeriodToggle}
                                disabled={isSubmitting}
                            >
                                Meia
                            </PeriodButton>
                            <PeriodButton
                                className={period === 'fulltime' ? 'active' : ''}
                                onClick={handlePeriodToggle}
                                disabled={isSubmitting}
                            >
                                Inteira
                            </PeriodButton>
                        </Box>
                    </Box>
                </Box>

                {/* Error message */}
                {errors.submit && (
                    <Typography variant="body2" color="error" align="center">
                        {errors.submit}
                    </Typography>
                )}
            </Box>
        </BaseModal>
    );
};

AddVisitRuleModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    idEnterprise: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    collaborators: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string
        })
    ),
    properties: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string
        })
    ),
    onSuccess: PropTypes.func
};
